// Colour-theme resolution — the single canonical implementation.
// public/index.html carries a tiny pre-paint copy of resolveTheme()'s rule
// (and of THEME_STORAGE_KEY) so the first paint already has the right theme;
// keep the two in step.
//
// Precedence: the user's stored choice > the deployment default
// (REACT_APP_DEFAULT_THEME) > the OS preference.

export const THEMES = ["light", "dark"];
export const THEME_PREFERENCES = ["system", ...THEMES];
export const THEME_STORAGE_KEY = "ds-theme";
const DARK_QUERY = "(prefers-color-scheme: dark)";

export function normalizePreference(value) {
  if (value === undefined || value === null || value === "") return "system";
  if (THEME_PREFERENCES.includes(value)) return value;
  console.warn(`[theme] ignoring unknown theme preference "${value}"; using "system"`);
  return "system";
}

export function resolveTheme(stored, configured, prefersDark) {
  if (THEMES.includes(stored)) return stored;
  const preference = normalizePreference(configured);
  if (preference !== "system") return preference;
  return prefersDark ? "dark" : "light";
}

export function readStoredTheme() {
  try {
    const value = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (value === null || THEMES.includes(value)) return value;
    console.warn(`[theme] discarding unknown stored theme "${value}"`);
    window.localStorage.removeItem(THEME_STORAGE_KEY);
    return null;
  } catch (err) {
    console.warn("[theme] could not read the stored theme", err);
    return null;
  }
}

export function writeStoredTheme(theme) {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch (err) {
    console.warn("[theme] could not persist the theme choice", err);
  }
}

function darkQuery() {
  return typeof window.matchMedia === "function" ? window.matchMedia(DARK_QUERY) : null;
}

export function systemPrefersDark() {
  return Boolean(darkQuery()?.matches);
}

export function subscribeSystemTheme(onChange) {
  const query = darkQuery();
  if (!query) return () => {};
  const listener = (event) => onChange(Boolean(event.matches));
  query.addEventListener("change", listener);
  return () => query.removeEventListener("change", listener);
}

export function applyTheme(theme) {
  const root = document.documentElement;
  root.dataset.theme = theme;
  root.style.colorScheme = theme;
}
